import React from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  useTheme
} from '@mui/material';
import { Warning as AlertTriangle, Close as X } from '@mui/icons-material';

// Styled Dialog for consistent styling
const StyledDialog = styled(Dialog)(({ theme }) => {
  const isDarkMode = theme.palette.mode === 'dark';
  
  return {
    '& .MuiDialog-paper': {
      borderRadius: '16px',
      boxShadow: '0 24px 48px rgba(0, 0, 0, 0.2)',
      background: isDarkMode 
        ? 'linear-gradient(135deg, rgba(30, 30, 30, 0.95), rgba(42, 42, 42, 0.95))'
        : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(240, 245, 255, 0.95))',
      backdropFilter: 'blur(10px)',
      overflow: 'hidden',
      [theme.breakpoints.down('sm')]: {
        margin: '16px',
        width: 'calc(100% - 32px)',
        maxWidth: '100%',
      }
    }
  };
});

const DialogHeader = styled(DialogTitle)(({ theme }) => {
  const isDarkMode = theme.palette.mode === 'dark';
  
  return {
    background: isDarkMode 
      ? 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)'
      : 'linear-gradient(135deg, #f43f5e 0%, #e11d48 100%)',
    color: 'white',
    padding: theme.spacing(2, 3),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  };
});

/**
 * A reusable delete confirmation dialog component
 */
const DeleteDialog = ({ 
  open, 
  onClose, 
  onConfirm,
  title,
  message,
  loading = false
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  // Use provided title/message or default to translations
  const dialogTitle = title || t('user.delete.title');
  const dialogMessage = message || t('user.delete.message');

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogHeader>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AlertTriangle fontSize="small" />
          <Typography variant="h6" component="div" sx={{ fontWeight: 600, fontSize: '1.1rem', color: 'white' }}>
            {dialogTitle}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}>
          <X fontSize="small" />
        </IconButton>
      </DialogHeader>
      
      <DialogContent sx={{ 
        pt: 3, 
        pb: 2,
        backgroundColor: isDarkMode ? '#1e1e1e' : '#ffffff'
      }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center',
          pb: 2
        }}>
          <Box sx={{ 
            mb: 2,
            p: 2,
            backgroundColor: isDarkMode 
              ? 'rgba(220, 38, 38, 0.2)' 
              : 'rgba(244, 63, 94, 0.1)',
            borderRadius: '50%',
          }}>
            <AlertTriangle sx={{ fontSize: 40, color: '#f43f5e' }} />
          </Box>
          <Typography 
            variant="h6" 
            gutterBottom 
            align="center" 
            sx={{ 
              fontWeight: 600,
              color: '#f43f5e'
            }}
          >
            {dialogTitle}
          </Typography>
          <Typography 
            variant="body1" 
            align="center" 
            sx={{ 
              mb: 2,
              color: isDarkMode ? '#ffffff' : 'inherit'
            }}
          >
            {dialogMessage}
          </Typography>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ 
        p: 3, 
        pt: 0,
        backgroundColor: isDarkMode ? '#2a2a2a' : '#ffffff',
        borderTop: `1px solid ${isDarkMode ? '#404040' : 'rgba(0, 0, 0, 0.12)'}`
      }}>
        <Button 
          onClick={onClose} 
          variant="outlined"
          disabled={loading}
          sx={{ 
            borderRadius: '10px', 
            color: isDarkMode ? '#ffffff' : '#667eea',
            borderColor: isDarkMode ? '#404040' : 'rgba(102, 126, 234, 0.5)',
            '&:hover': {
              borderColor: isDarkMode ? '#667eea' : '#667eea',
              backgroundColor: isDarkMode 
                ? 'rgba(102, 126, 234, 0.1)' 
                : 'rgba(102, 126, 234, 0.05)',
            }
          }}
        >
          {t('common.cancel')}
        </Button>
        <Button 
          onClick={onConfirm} 
          variant="contained" 
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <AlertTriangle fontSize="small" />}
          disabled={loading}
          sx={{ 
            borderRadius: '10px',
            backgroundColor: '#f43f5e',
            '&:hover': {
              backgroundColor: '#e11d48',
            }
          }}
        >
          {loading ? t('user.delete.deleting') : t('common.delete')}
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

export default DeleteDialog;